import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import AppSelect from 'src/components/common/AppSelect';
import { Switch } from 'src/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';

const customerTypes = [
  { value: 'Tier 1', label: 'Tier 1' },
  { value: 'Tier 2', label: 'Tier 2' },
  { value: 'Tier 3', label: 'Tier 3' },
];

const customerCategories = [
  { value: 'Category A', label: 'Category A' },
  { value: 'Category B', label: 'Category B' },
];

const defaultFormData = {
  name: '',
  description: '',
  customer_type: '',
  customer_category: '',
  address_line_1: '',
  street: '',
  zip_code: '',
  gstin: '',
};

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const SectionLabel = ({ children }) => (
  <p className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-muted-foreground mb-3 pt-1">
    {children}
  </p>
);

const fieldBase =
  'w-full h-9 px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

export const OrganizationModal = ({
  onSubmit,
  onCancel,
  initialData,
  loading,
}) => {
  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...(initialData && {
      ...initialData,
      is_active: initialData.is_active ?? true,
    }),
  });
  const [gstinError, setGstinError] = useState('');

  const isEditMode = Boolean(initialData?.id);
  const buttonText = isEditMode ? 'Save Changes' : 'Create Organization';
  const loadingText = isEditMode ? 'Saving...' : 'Creating...';

  const set = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const validateGSTIN = (gstin) => {
    const value = gstin?.trim() ?? '';
    if (!value) {
      setGstinError('GSTIN is required');
      return false;
    }
    if (value.length !== 15) {
      setGstinError('GSTIN must be exactly 15 characters');
      return false;
    }
    setGstinError('');
    return true;
  };

  const hasMissingRequiredFields = () => {
    const required = [
      'name',
      'description',
      'customer_type',
      'customer_category',
      'address_line_1',
      'street',
      'zip_code',
      'gstin',
    ];
    return required.some((f) => {
      const v = formData[f];
      return typeof v === 'string' ? !v.trim() : !v;
    });
  };

  const isGSTINValid = formData.gstin?.trim().length === 15 && !gstinError;
  const isFormValid = !hasMissingRequiredFields() && isGSTINValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateGSTIN(formData.gstin)) return;
    if (!isFormValid) return;

    const baseData = {
      name: formData.name,
      description: formData.description,
      customer_type: formData.customer_type,
      customer_category: formData.customer_category,
      address_line_1: formData.address_line_1,
      street: formData.street,
      zip_code: formData.zip_code,
      gstin: formData.gstin,
    };

    onSubmit(
      isEditMode ? { ...baseData, is_active: formData.is_active } : baseData
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-[22px] py-[18px]">
        {/* Section — Organisation Details */}
        <SectionLabel>Organisation Details</SectionLabel>

        <div className="mb-4">
          <FieldLabel htmlFor="name" required>
            Organisation Name
          </FieldLabel>
          <input
            id="name"
            className={fieldBase}
            value={formData.name}
            onChange={set('name')}
            placeholder="e.g. HDFC Bank Ltd."
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <FieldLabel htmlFor="description" required>
            Description
          </FieldLabel>
          <textarea
            id="description"
            className={cn(fieldBase, 'resize-y min-h-[80px] leading-[1.65]')}
            value={formData.description}
            onChange={set('description')}
            placeholder="Brief description of the organisation…"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <FieldLabel htmlFor="customerType" required>
              Customer Type
            </FieldLabel>
            <AppSelect
              options={customerTypes}
              value={formData.customer_type || undefined}
              onValueChange={(v) =>
                setFormData((p) => ({ ...p, customer_type: v }))
              }
              disabled={loading}
              id="customerType"
              placeholder="Select type"
              className="h-9"
            />
          </div>
          <div>
            <FieldLabel htmlFor="customerCategory" required>
              Customer Category
            </FieldLabel>
            <AppSelect
              options={customerCategories}
              value={formData.customer_category || undefined}
              onValueChange={(v) =>
                setFormData((p) => ({ ...p, customer_category: v }))
              }
              disabled={loading}
              id="customerCategory"
              placeholder="Select category"
              className="h-9"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-3" />

        {/* Section — Address & Compliance */}
        <SectionLabel>Address &amp; Compliance</SectionLabel>

        <div className="mb-4">
          <FieldLabel htmlFor="addressLine1" required>
            Address Line 1
          </FieldLabel>
          <input
            id="addressLine1"
            className={fieldBase}
            value={formData.address_line_1}
            onChange={set('address_line_1')}
            placeholder="Building, floor, unit…"
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <FieldLabel htmlFor="street" required>
            Street
          </FieldLabel>
          <input
            id="street"
            className={fieldBase}
            value={formData.street}
            onChange={set('street')}
            placeholder="Street name"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <FieldLabel htmlFor="zipCode" required>
              Zip Code
            </FieldLabel>
            <input
              id="zipCode"
              className={fieldBase}
              value={formData.zip_code}
              onChange={set('zip_code')}
              placeholder="e.g. 400001"
              disabled={loading}
            />
          </div>
          <div>
            <FieldLabel htmlFor="gstin" required>
              GSTIN
            </FieldLabel>
            <input
              id="gstin"
              className={cn(
                fieldBase,
                'font-mono text-[12.5px] tracking-[.02em]',
                gstinError &&
                  'border-destructive focus:border-destructive focus:ring-destructive/10'
              )}
              value={formData.gstin}
              onChange={(e) => {
                setFormData((p) => ({ ...p, gstin: e.target.value }));
                validateGSTIN(e.target.value);
              }}
              placeholder="29ABCDE1234F1Z6"
              maxLength={15}
              disabled={loading}
            />
            {gstinError && (
              <p className="text-[11.5px] text-destructive mt-1">
                {gstinError}
              </p>
            )}
          </div>
        </div>

        {isEditMode && (
          <>
            <div className="border-t border-border my-3" />
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData((p) => ({ ...p, is_active: checked }))
                }
                disabled={loading}
              />
              <span className="text-[13px] font-medium text-foreground">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Pinned footer */}
      <div className="flex justify-end gap-2 px-[22px] py-[14px] border-t border-border bg-brand-bg-subtle shrink-0">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading || !isFormValid}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </form>
  );
};

export default OrganizationModal;
